import { Link } from 'react-router-dom'
import { AlertTriangle, Clock } from 'lucide-react'
import { useSubscription } from '@/stores/authStore'
import { useCan } from '@/hooks/useCan'

/**
 * Trial and lapse messaging.
 *
 * Deliberately a banner rather than a modal. The POS keeps selling through
 * the grace period (ADR-008), so blocking the screen to demand payment would
 * contradict the behaviour the customer is actually experiencing.
 *
 * The trial notice only appears in the last week — a countdown from day one
 * is noise that people learn to ignore before it matters.
 */
export function SubscriptionBanner() {
  const subscription = useSubscription()
  const canManageBilling = useCan('billing.manage')

  if (!subscription) return null

  const days = subscription.days_remaining

  if (subscription.status === 'trialing' && days !== null && days <= 7) {
    return (
      <Notice tone="warning" icon={<Clock className="h-4 w-4" strokeWidth={2} />}>
        {days === 0 ? 'Your trial ends today.' : `${days} day${days === 1 ? '' : 's'} left in your trial.`}{' '}
        {canManageBilling && (
          <Link to="/billing" className="font-semibold underline underline-offset-2">
            Choose a plan
          </Link>
        )}
      </Notice>
    )
  }

  if (subscription.status === 'past_due') {
    return (
      <Notice tone="warning" icon={<AlertTriangle className="h-4 w-4" strokeWidth={2} />}>
        <span className="font-semibold">Payment overdue.</span> You can keep selling
        {days !== null && ` for ${days} more day${days === 1 ? '' : 's'}`}, but new products and
        staff are paused.{' '}
        {canManageBilling && (
          <Link to="/billing" className="font-semibold underline underline-offset-2">
            Renew now
          </Link>
        )}
      </Notice>
    )
  }

  if (subscription.status === 'expired' || subscription.status === 'cancelled') {
    return (
      <Notice tone="critical" icon={<AlertTriangle className="h-4 w-4" strokeWidth={2} />}>
        <span className="font-semibold">Your subscription has ended.</span> Your data is safe and
        can still be exported.{' '}
        {canManageBilling && (
          <Link to="/billing" className="font-semibold underline underline-offset-2">
            Reactivate
          </Link>
        )}
      </Notice>
    )
  }

  return null
}

function Notice({
  tone,
  icon,
  children,
}: {
  tone: 'warning' | 'critical'
  icon: React.ReactNode
  children: React.ReactNode
}) {
  const styles =
    tone === 'critical'
      ? 'bg-critical-subtle text-critical border-critical/20'
      : 'bg-warning-subtle text-[#8a5f00] border-warning/30'

  return (
    <div className={`flex items-center gap-2.5 border-b px-6 py-2 text-sm ${styles}`}>
      <span className="shrink-0" aria-hidden>
        {icon}
      </span>
      <p>{children}</p>
    </div>
  )
}
